import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../lib/apiBase';

const WishlistContext = createContext();
const STORAGE_KEY = 'sv_wishlist';
const CLIENT_ID_KEY = 'sv_client_id';
const MIGRATION_DONE_KEY = 'sv_wishlist_migrated_v1';
const MAX = 9;

/**
 * Generate or read a stable per-browser client ID. We never tie this to a
 * Google account — it's just enough entropy to keep one user's wishlist
 * grouped across page loads, devices being a separate concern (the user
 * has to copy the link / use the same browser to roam).
 */
function getOrMintClientId() {
  if (typeof window === 'undefined') return null;
  try {
    let cid = window.localStorage.getItem(CLIENT_ID_KEY);
    if (cid) return cid;
    // crypto.randomUUID is available in every browser we target (Chrome 92+,
    // Safari 15.4+, Firefox 95+). Fall back to a manual UUID v4 just in case.
    cid = (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });
    window.localStorage.setItem(CLIENT_ID_KEY, cid);
    return cid;
  } catch {
    return null;
  }
}

function readLegacyLocalWishlist() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function WishlistProvider({ children }) {
  // Synchronously hydrate from the legacy localStorage cache so the very
  // first paint shows the saved wishlist instantly. The server fetch
  // overrides shortly after with the canonical list.
  const [wishlist, setWishlist] = useState(readLegacyLocalWishlist);
  const clientIdRef = useRef(getOrMintClientId());

  const persistLocal = useCallback((next) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* quota / private mode — fine */
    }
  }, []);

  // 1. On mount: migrate any pre-existing local wishlist to the server,
  //    then fetch the canonical list. The server response is the source of
  //    truth from this point on.
  useEffect(() => {
    const cid = clientIdRef.current;
    if (!cid) return;

    let cancelled = false;

    (async () => {
      try {
        if (!window.localStorage.getItem(MIGRATION_DONE_KEY)) {
          const local = readLegacyLocalWishlist();
          if (local.length) {
            await fetch(`${BACKEND_URL}/api/wishlist/migrate`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                client_id: cid,
                experience_ids: local.map((e) => e.id).filter(Boolean),
              }),
            }).catch(() => {});
          }
          window.localStorage.setItem(MIGRATION_DONE_KEY, '1');
        }
        const res = await fetch(`${BACKEND_URL}/api/wishlist?client_id=${encodeURIComponent(cid)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const list = data.experiences || [];
        setWishlist(list);
        persistLocal(list);
      } catch {
        /* offline / 5xx — keep the local hydrate in place silently */
      }
    })();

    return () => { cancelled = true; };
  }, [persistLocal]);

  // Server is source of truth, but we still mirror to localStorage so a
  // hard reload paints instantly.
  useEffect(() => {
    persistLocal(wishlist);
  }, [wishlist, persistLocal]);

  /**
   * Server-authoritative toggle. We optimistically update the in-memory
   * list, then call the API; on failure we revert. Network blips are
   * surfaced via a sonner toast.
   */
  const toggleWishlist = useCallback(async (experience) => {
    const cid = clientIdRef.current;
    if (!cid || !experience || !experience.id) return;

    const wasIn = wishlist.some((e) => e.id === experience.id);
    if (!wasIn && wishlist.length >= MAX) {
      toast.error(`Wishlist is full (max ${MAX}).`);
      return;
    }
    const optimistic = wasIn
      ? wishlist.filter((e) => e.id !== experience.id)
      : [...wishlist, experience];
    setWishlist(optimistic);

    try {
      const res = await fetch(`${BACKEND_URL}/api/wishlist/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: cid, experience_id: experience.id }),
      });
      if (!res.ok) {
        // Roll back on failure.
        setWishlist(wishlist);
        if (res.status === 409) {
          toast.error(`Wishlist is full (max ${MAX}).`);
        } else if (res.status === 404) {
          toast.error('That experience is no longer available.');
        } else {
          toast.error('Could not update wishlist. Please retry.');
        }
      }
    } catch {
      setWishlist(wishlist);
      toast.error('Could not update wishlist. Please retry.');
    }
  }, [wishlist]);

  const addToWishlist = useCallback((experience) => {
    if (!wishlist.some((e) => e.id === experience.id)) {
      toggleWishlist(experience);
    }
  }, [wishlist, toggleWishlist]);

  const removeFromWishlist = useCallback((id) => {
    const exp = wishlist.find((e) => e.id === id);
    if (exp) toggleWishlist(exp);
  }, [wishlist, toggleWishlist]);

  const isInWishlist = useCallback((id) => wishlist.some((e) => e.id === id), [wishlist]);

  return (
    <WishlistContext.Provider value={{ wishlist, addToWishlist, removeFromWishlist, isInWishlist, toggleWishlist }}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const context = useContext(WishlistContext);
  if (!context) throw new Error('useWishlist must be used within WishlistProvider');
  return context;
}
